import { styled } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { AdminSolution, CommodityManagementSolution, Dashboard, Header, MarketViewSolution, SidebarNavigation } from '.';
import { MarkItViewApi } from '../Apis/Apis';
import OktaLoginCallback from '../Core/Okta/OktaLoginCallback';
import OktaSecurity from '../Core/Okta/OktaSecurity';
import { useLoadingState } from '../Hooks';
import store, { RootState, useApplicationDispatch, useApplicationSelector } from '../Redux/ReduxStore';
import { UserAccessState, setUserAccess } from '../Redux/Slices/UserAccessSlice';
import './App.scss';
import LoginPage from './LoginPage';
import Footer from './MainContainer/Footer';
import TermsAndConditions from './TermsAndConditions';
import ForecastTool from './MainContainer/Tools/Forecast/Forecast';
import EiaGasStorageTool from './MainContainer/Tools/EiaGasStorage/EiaGasStorageTool';
import HedgePlans from './MainContainer/Tools/HedgePlans/HedgePlans';
import MatrixTool from './MainContainer/Tools/Matrix/MatrixTool';
import UserDisabledPage from './UserDisabledPage';
import AuthScaffolding from './AuthScaffolding/AuthScaffolding';
import { ChatResultsProvider } from './MainContainer/CommodityManagement/Dashboard/ChatResultsContext';
import { ChatUiProvider } from './MainContainer/CommodityManagement/Dashboard/ChatUiContext';

function AuthWrapper(props: { children: React.ReactNode }) {
  const { authState, oktaAuth } = useOktaAuth();
  const prevTokenRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const dispatch = useApplicationDispatch();
  const authorizationLoadingState = useLoadingState();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);

  function authorizeUser() {
    oktaAuth.getUser().then((userInfo) => {
      // If authorizationLoadingState is already loaded, then don't update the loading state. It will force the page to refresh.
      MarkItViewApi.marketViewUserAuthorizationCallback(authorizationLoadingState.isLoaded() ? undefined : authorizationLoadingState.setLoadingState).then(
        (res) => {
          if (res.data.user?.isDisabled) {
            // If the user is disabled, redirect to the disabled page.
            navigate('/disabled');
            return;
          }

          dispatch(
            setUserAccess({
              email: res.data.user?.email ?? undefined,
              displayName: res.data.user?.displayName,
              companyGuid: res.data.user?.companyGuid,
              companyName: res.data.user?.company?.name,
              commodityManagementUserSiteAccesses: res.data.user?.commodityManagementUserSiteAccesses,
              marketViewApplications: res.data.user?.company?.marketViewApplications ?? [],
              productAccess: res.data.user?.company?.productAccess ?? [],
              hasAccess: res.data.user?.email == null ? false : true,
              isAdmin: res.data.user?.isAdministrator,
              agreedToTermsAndConditions: res.data.user?.getTermsAndConditionsResponse?.agreed,
              isEmployee: userInfo['user_type'] === 'Employee',
              isDisabled: res.data.user?.isDisabled ?? false,
            }),
          );
        },
      );
    });
  }

  useEffect(() => {
    if (!authState) {
      return;
    }

    if (!authState.isAuthenticated) {
      navigate('/login');
    }

    const currentToken = authState.accessToken?.accessToken || null;

    if (prevTokenRef.current !== currentToken) {
      prevTokenRef.current = currentToken;
      authorizeUser();
    }
  }, [authState, oktaAuth]);

  if (authorizationLoadingState.isLoading()) {
    return <AuthScaffolding />; // Authorizing
  }

  if (authorizationLoadingState.isLoaded()) {
    if (!userAccess.agreedToTermsAndConditions) {
      return <TermsAndConditions />;
    }

    return <>{props.children}</>;
  }

  return <></>; // Loading
}

function StandardLayout() {
  return (
    <AuthWrapper>
      <ChatUiProvider>
        <ChatResultsProvider>
          <Header />
          <div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
            <SidebarNavigation />
            <main>
              <div className="main-outlet">
                <Outlet />
              </div>
              <Footer />
            </main>
          </div>
        </ChatResultsProvider>
      </ChatUiProvider>
    </AuthWrapper>
  );
}

export default function App() {
  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    '&.notistack-MuiContent-success': {
      backgroundColor: 'var(--toast-success-background-color)',
      color: 'var(--toast-success-text-color)',
    },
  }));

  return (
    <SnackbarProvider Components={{ success: StyledMaterialDesignContent }} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Provider store={store}>
        <OktaSecurity>
          <BrowserRouter>
            <Routes>
              <Route>
                <Route element={<Outlet />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/logout" element={<Navigate to="/login" replace />} />
                  <Route path="/disabled" element={<UserDisabledPage />} />
                </Route>
                <Route element={<StandardLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/commodity-management/*" element={<CommodityManagementSolution />} />
                  <Route path="/tools/market-view" element={<MarketViewSolution />} />
                  <Route path="/tools/forecast" element={<ForecastTool />} />
                  <Route path="/tools/matrix" element={<MatrixTool />} />
                  <Route path="/tools/hedge-plans" element={<HedgePlans />} />
                  <Route path="/tools/eia-gas-storage" element={<EiaGasStorageTool />} />
                  <Route path="/admin/*" element={<AdminSolution />} />
                </Route>
              </Route>
              <Route path="/callback" element={<OktaLoginCallback />} />
            </Routes>
            {/* <MainLayout /> */}
          </BrowserRouter>
        </OktaSecurity>
      </Provider>
    </SnackbarProvider>
  );
}
